import React, { useState } from "react";
import { Contact } from "../types/Contact";
import { StateAbbreviations } from "../types/States";
import Col100 from "./Col100";
import Header from "./Header";
import Processing from "./Processing";
import { dynamicsService } from "../services/DynamicsService";

type Props = {
  checkoutId: string;
  onComplete: ((clientId: string, contact: Contact) => void);
};
export default function ClientInformation(props: Props): JSX.Element {
  const { checkoutId, onComplete } = props;
  const [inputs, setInputs] = useState({
    firstName: "",
    lastName: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "MD",
    zipcode: "",
    telephone: "",
    dob: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [warning, setWarning] = useState<string>();
  const [clientID, setClientID] = useState<string>();
  // Accessors
  const {
    firstName,
    lastName,
    email,
    address1,
    address2,
    city,
    state,
    zipcode,
    telephone,
    dob
  } = inputs;
  // Event Handlers
  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) {
    const { name, value } = e.target;
    // setIsDirty(true);
    setInputs(inputs => ({ ...inputs, [name]: value }));
  }
  function handleSubmit(e: React.FormEvent) {
    console.log("handleSubmit", e);
    e.preventDefault();
    // TODO Validation
    setSubmitted(true);
    setError(undefined);
    if (
      !firstName ||
      !lastName ||
      !address1 ||
      !city ||
      !state ||
      !zipcode ||
      !telephone ||
      !email ||
      !dob
    ) {
      setWarning(
        "Please complete all of the required fields in order to continue"
      );
      return;
    }

    // TODO submit
    let contact = {
      firstName,
      lastName,
      address1,
      address2,
      city,
      state,
      zipcode,
      telephone,
      email,
      dob
    };

    // Advance to Step 2 by calling the onComplete callback handler in App
    setSubmitting(true);
    dynamicsService
      .createOrUpdateClient(checkoutId, contact)
      .then(clientContact => {
        setClientID(clientContact);
        onComplete(clientContact, contact);
      })
      .catch(reason => {
        console.error(reason);
        setSubmitting(false);
        setError(reason);
      });
  }
  if (error) {
    return <Col100>
        <Header>Error!</Header>
        <div className="elementor-element elementor-widget elementor-widget-text-editor" data-element_type="widget" data-widget_type="text-editor.default">
          <div className="elementor-widget-container">
            <div className="elementor-text-editor elementor-clearfix">
              <p>
                We're sorry-- but there was a problem when saving your contact information: {error}.
              </p>
              <button type="submit" className="da-button flex-grow-1" onClick={() => setError(undefined)}>
                <span className="mr-1">Retry</span>
              </button>
            </div>
          </div>
        </div>
      </Col100>;
  }

  if (submitting) {
    return (
      <Col100>
        <Processing />
      </Col100>
    );
  }
  return <Col100>
      <Header>SafeHaven Member Information</Header>
      <div className="elementor-element elementor-widget elementor-widget-text-editor" data-element_type="widget" data-widget_type="text-editor.default">
        <div className="elementor-widget-container">
          <div className="elementor-text-editor elementor-clearfix">
            <p>
              Please provide us the following information.
            </p>
          </div>
        </div>
      </div>
      {error && <Col100>
          <strong>ERROR!</strong> {error}
        </Col100>}
      <form onSubmit={handleSubmit} className="order-form elementor-form" id="orderForm" name="OrderForm">
        <div className="">
          <div className="d-flex">
            <div className="flex-grow-1 mr-1">
              <label htmlFor="firstName" className="">
                First Name
              </label>
              <input size={1} type="text" id="firstName" name="firstName" autoComplete="given-name" className={"input da-plugin" + (submitted && !firstName ? " is-invalid" : "")} placeholder="First Name" required={true} aria-required="true" value={firstName} onChange={handleChange} />
            </div>
            <div className="flex-grow-1">
              <label htmlFor="lastName" className="">
                Last Name
              </label>
              <input size={1} type="text" name="lastName" id="lastName" autoComplete="family-name" className={"input da-plugin" + (submitted && !lastName ? " is-invalid" : "")} placeholder="Last Name" required={true} aria-required="true" value={lastName} onChange={handleChange} />
            </div>
          </div>
          <div className="mt-2">
            <div className="">
              <label htmlFor="address1" className="">
                Street Address
              </label>
              <input size={1} type="text" name="address1" id="address1" autoComplete="address-line1" className="input da-plugin" placeholder="Street Address" required={true} aria-required="true" value={address1} onChange={handleChange} />
            </div>
            <div className="mt-1">
              <input size={1} type="text" name="address2" autoComplete="address-line2" id="address2" className="input da-plugin" placeholder="Apartment, Suite, Etc." required={false} aria-required="true" value={address2} onChange={handleChange} />
            </div>
          </div>
          <div className="d-flex mt-2">
            <div className="flex-grow-1">
              <label htmlFor="city" className="">
                City
              </label>
              <input size={1} type="text" name="city" autoComplete="address-level2" id="city" className="input da-plugin" placeholder="City" required={true} aria-required="true" value={city} onChange={handleChange} />
            </div>
            <div className="elementor-field-type-select elementor-field-group elementor-column elementor-field-group-field_348f702 elementor-col-25 elementor-field-required">
              <label htmlFor="state" className="">
                State
              </label>
              <div className="elementor-field elementor-select-wrapper ">
                <select name="state" id="state" autoComplete="address-level1" className="input da-plugin" required={true} aria-required="true" onChange={handleChange} value={state}>
                  <option value="">State</option>
                  {Object.keys(StateAbbreviations()).map(state =>
                    <option key={state} value={state}>
                      {state}
                    </option>
                  )}
                </select>
              </div>
            </div>
            <div className="">
              <label htmlFor="zipcode" className="">
                Zipcode
              </label>
              <div>
                <input type="number" name="zipcode" id="zipcode" autoComplete="postal-code" className="input da-plugin" placeholder="Zipcode" required={true} aria-required="true" min="0" max="99999" onChange={handleChange} value={zipcode} />
              </div>
            </div>
          </div>
          <div className="d-flex mt-2">
            <div className="flex-grow-1 mr-1">
              <label htmlFor="email" className="">
                Email
              </label>
              <input size={1} type="email" name="email" autoComplete="email" id="email" className="input da-plugin" placeholder="Email" required={true} aria-required="true" onChange={handleChange} value={email} />
            </div>
            <div className="flex-grow-1 mr-1">
              <label htmlFor="telephone" className="">
                Telephone
              </label>
              <input size={1} type="text" name="telephone" autoComplete="tel" id="telephone" className="input da-plugin" placeholder="Telephone" onChange={handleChange} value={telephone} />{" "}
            </div>
            <div className="">
              <label htmlFor="dob" className="elementor-field-label" style={{ paddingRight: "10px" }}>
                Date of Birth
              </label>
              <div>
                <input type="date" name="dob" id="dob" autoComplete="bday" className="input da-plugin" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}" min="1910-12-01" onChange={handleChange} value={dob} required={true} />
              </div>
            </div>
          </div>
          {warning && <div className="elementor-element elementor-widget elementor-widget-text-editor" data-element_type="widget" data-widget_type="text-editor.default">
              <div className="elementor-widget-container">
                <div className="elementor-text-editor elementor-clearfix">
                  <p className="text-danger">
                    {warning}
                  </p>
                </div>
              </div>
            </div>}
          <div className="mt-2 d-flex">
            <button type="submit" className="da-button flex-grow-1">
                <span className="mr-1">Next</span>
                <i aria-hidden="true" className="fas fa-angle-right" />
            </button>
          </div>
        </div>
      </form>
    </Col100>;
}
